import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditActionDialog = ({ action, open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [savingSynthese, setSavingSynthese] = useState(false);
  const [formData, setFormData] = useState({
    type_action: '',
    titre: '',
    date: '',
    heure_debut: '',
    heure_fin: '',
    notes: '',
    zone_geographique: ''
  });
  const [synthese, setSynthese] = useState('');
  const [syntheseMeta, setSyntheseMeta] = useState({ par: '', date: '' });

  useEffect(() => {
    if (action) {
      setFormData({
        type_action: action.type_action || '',
        titre: action.titre || '',
        date: action.date || '',
        heure_debut: action.heure_debut || '',
        heure_fin: action.heure_fin || '',
        notes: action.notes || '',
        zone_geographique: action.zone_geographique || ''
      });
      setSynthese(action.synthese || '');
      setSyntheseMeta({
        par: action.synthese_redigee_par_nom || '',
        date: action.synthese_date || ''
      });
    }
  }, [action]);

  const isRdvPasse = () => {
    if (!action?.date) return false;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const d = new Date(action.date);
      return d <= today;
    } catch {
      return false;
    }
  };

  const handleSaveSynthese = async () => {
    setSavingSynthese(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/actions/${action.id}/synthese`,
        { synthese },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Synthèse enregistrée');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement de la synthèse');
    } finally {
      setSavingSynthese(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/actions/${action.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Action modifiée avec succès');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de modification');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette action ?')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/actions/${action.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Action supprimée');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Erreur de suppression');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] rounded-3xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>
            Modifier l'action
          </DialogTitle>
          <DialogDescription>
            Modifiez les détails de l'action ou du rendez-vous
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Type d'action</Label>
            <Select 
              value={formData.type_action} 
              onValueChange={(val) => setFormData({ ...formData, type_action: val })}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rdv_individuel">RDV Individuel</SelectItem>
                <SelectItem value="formation">Formation</SelectItem>
                <SelectItem value="accompagnement">Accompagnement</SelectItem>
                <SelectItem value="atelier">Atelier</SelectItem>
                <SelectItem value="entretien">Entretien</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={formData.titre}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              required
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Zone</Label>
              <Select 
                value={formData.zone_geographique} 
                onValueChange={(val) => setFormData({ ...formData, zone_geographique: val })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                  <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Heure de début</Label>
              <Input
                type="time"
                value={formData.heure_debut}
                onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Heure de fin</Label>
              <Input
                type="time"
                value={formData.heure_fin}
                onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="rounded-xl"
              placeholder="Commentaires, observations..."
            />
          </div>

          {/* Synthèse de l'entretien — visible pour les RDV individuels */}
          <div className="space-y-2 p-4 bg-indigo-50 border border-indigo-200 rounded-2xl">
            <div className="flex items-center justify-between">
              <Label className="text-indigo-900 font-semibold">
                Synthèse de l'entretien
              </Label>
              {syntheseMeta.par && (
                <span className="text-xs text-indigo-600">
                  Rédigée par {syntheseMeta.par}
                  {syntheseMeta.date && ` le ${new Date(syntheseMeta.date).toLocaleDateString('fr-FR')}`}
                </span>
              )}
            </div>
            <p className="text-xs text-indigo-700">
              Note interne d'accompagnement — non visible par le bénéficiaire.
              {isRdvPasse() && !synthese && (
                <span className="block mt-1 font-semibold text-amber-700">
                  ⚠️ Pense-bête : ce RDV est passé, pensez à rédiger sa synthèse.
                </span>
              )}
            </p>
            <Textarea
              value={synthese}
              onChange={(e) => setSynthese(e.target.value)}
              className="rounded-xl bg-white"
              placeholder="Points abordés, décisions prises, prochaines étapes..."
              rows={4}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSaveSynthese}
              disabled={savingSynthese}
              className="rounded-xl bg-white border-indigo-300 text-indigo-700 hover:bg-indigo-100"
            >
              {savingSynthese ? 'Enregistrement…' : 'Enregistrer la synthèse'}
            </Button>
          </div>

          <DialogFooter className="flex justify-between">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleDelete} 
              disabled={loading}
              className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
            >
              Supprimer
            </Button>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)} 
                className="rounded-xl"
              >
                Annuler
              </Button>
              <Button type="submit" disabled={loading} className="rounded-xl">
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditActionDialog;
